import React, { Component } from 'react';
import PropTypes from 'prop-types';
import Box from '@mui/material/Box';
import Grid from '@mui/material/Grid';
import Typography from '@mui/material/Typography';
import { PieChart, Pie, Cell, ResponsiveContainer } from 'recharts';
import ToolTip from './ToolTip';

const RAD = Math.PI / 180;

/** In-chart slice labels — sans-serif 12px, black (SVG text). */
function pieSliceLabel(props) {
  const {
    cx = 0,
    cy = 0,
    midAngle = 0,
    innerRadius = 0,
    outerRadius = 0,
    name,
  } = props;

  const display = name != null ? String(name) : '';
  if (!display) {
    return null;
  }

  const ir = Number(innerRadius) || 0;
  const or = Number(outerRadius) || 0;
  const r = ir + (or - ir) * 0.52;
  const x = cx + r * Math.cos(-midAngle * RAD);
  const y = cy + r * Math.sin(-midAngle * RAD);

  return (
    <text
      x={x}
      y={y}
      fill="#000"
      textAnchor="middle"
      dominantBaseline="central"
      fontFamily="sans-serif"
      fontSize="12px"
    >
      {display}
    </text>
  );
}

/** Same as legacy react-easy-chart Legend with dataId "label": one row per distinct "name: value%". */
function dedupeLegendRows(chartRows) {
  const seen = new Set();
  const out = [];
  chartRows.forEach((row) => {
    const name = row.name != null ? String(row.name) : '';
    const key = `${name}:${row.value}`;
    if (seen.has(key)) return;
    seen.add(key);
    out.push(row);
  });
  return out;
}

class SharedPieChart extends Component {
  state = {
    showToolTip: false,
    top: '',
    left: '',
    value: '',
    key: '',
  };

  chartData = () => {
    const { itemsSelected, competitors = [] } = this.props;

    const idRank = new Map();
    competitors.forEach((c, i) => {
      if (c && c.id != null && c.id !== '') {
        idRank.set(String(c.id), i);
      }
    });

    const rows = itemsSelected.map((item, originalIndex) => {
      const raw = item.percentage;
      const value = typeof raw === 'number' && Number.isFinite(raw) ? raw : Number(raw);
      const safeValue = Number.isFinite(value) ? value : 0;
      return {
        key: item.selectedCompetitor.label != null ? item.selectedCompetitor.label : '',
        name: item.selectedCompetitor.value,
        value: safeValue,
        color: item.selectedCompetitor.color || '#DED9D9',
        id: item.selectedCompetitor.id,
        isOther: item.selectedCompetitor.isOther,
        originalIndex,
      };
    });

    const isUnselected = (r) => r.name === 'Unselected';
    const rest = rows.filter((r) => !isUnselected(r));
    const uns = rows.filter((r) => isUnselected(r));

    if (competitors.length > 0) {
      rest.sort((a, b) => {
        const ra = a.id != null && idRank.has(String(a.id)) ? idRank.get(String(a.id)) : 100000;
        const rb = b.id != null && idRank.has(String(b.id)) ? idRank.get(String(b.id)) : 100000;
        if (ra !== rb) return ra - rb;
        return a.originalIndex - b.originalIndex;
      });
    }

    return [...rest, ...uns].map(({ originalIndex, ...row }) => row);
  };

  mouseOverHandler = (data, _index, e) => {
    if (!e) return;
    this.setState({
      showToolTip: true,
      top: `${e.clientY - 10}px`,
      left: `${e.clientX + 10}px`,
      value: data.value,
      key: data.key,
    });
  };

  mouseMoveHandler = (e) => {
    if (this.state.showToolTip) {
      this.setState({ top: `${e.clientY}px`, left: `${e.clientX + 10}px` });
    }
  };

  mouseOutHandler = () => {
    this.setState({ showToolTip: false });
  };

  createTooltip = () => {
    if (this.state.showToolTip) {
      const message = this.state.key
        ? `${this.state.key}: ${this.state.value}%`
        : `${this.state.value}% remaining`;

      return (
        <ToolTip top={this.state.top} left={this.state.left}>
          {message}
        </ToolTip>
      );
    }

    return false;
  };

  handlePieClick = (data) => {
    const { chartClick } = this.props;
    if (!chartClick || !data) return;
    chartClick({
      data: {
        key: data.key !== undefined && data.key !== '' ? data.key : data.name,
        value: data.value,
        id: data.id,
        isOther: data.isOther,
      },
    });
  };

  render = () => {
    const data = this.chartData();
    const legendRows = dedupeLegendRows(data);
    const pieKey = data.map((d) => `${String(d.name)}:${d.value}:${d.color}`).join('|');

    return (
      <React.Fragment>
        {this.createTooltip()}

        <Grid
          container
          alignItems={{ xs: 'stretch', md: 'center' }}
          justifyContent="center"
          spacing={2}
          sx={{ width: '100%' }}
        >
          <Grid item xs={12} md={7} sx={{ maxWidth: '100%' }}>
            <div
              role="presentation"
              onMouseMove={this.mouseMoveHandler}
              onMouseLeave={this.mouseOutHandler}
              style={{ width: '100%', maxWidth: 400, height: 400, margin: '0 auto' }}
            >
              <ResponsiveContainer width="120%" height="120%">
                <PieChart>
                  <Pie
                    key={pieKey}
                    data={data}
                    dataKey="value"
                    nameKey="name"
                    cx="50%"
                    cy="50%"
                    innerRadius="50%"
                    outerRadius="76%"
                    paddingAngle={2}
                    label={pieSliceLabel}
                    labelLine={false}
                    isAnimationActive={false}
                    onClick={this.handlePieClick}
                    onMouseEnter={this.mouseOverHandler}
                    onMouseOut={this.mouseOutHandler}
                  >
                    {data.map((entry, i) => (
                      <Cell
                        key={`cell-${i}-${String(entry.name)}-${entry.value}`}
                        fill={entry.color}
                        stroke={entry.color}
                        strokeWidth={0}
                      />
                    ))}
                  </Pie>
                </PieChart>
              </ResponsiveContainer>
            </div>
          </Grid>

          <Grid
            item
            xs={12}
            md={5}
            sx={{
              minWidth: 0,
              display: 'flex',
              alignItems: { xs: 'flex-start', md: 'center' },
              justifyContent: { xs: 'center', md: 'flex-start' },
            }}
          >
            <Box sx={{ minWidth: 0, maxWidth: 340, width: '100%' }}>
              {legendRows.map((item, idx) => (
                <Box
                  key={`legend-${idx}-${String(item.name)}-${item.value}`}
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    mb: idx === legendRows.length - 1 ? 0 : 1.25,
                    wordBreak: 'break-word',
                  }}
                >
                  <Box
                    component="span"
                    sx={{
                      display: 'inline-block',
                      width: 11,
                      height: 11,
                      borderRadius: '50%',
                      mr: 1.25,
                      flexShrink: 0,
                      bgcolor: item.color,
                    }}
                  />
                  <Typography
                    component="span"
                    sx={{
                      fontFamily:
                        '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Oxygen, Ubuntu, Cantarell, "Fira Sans", "Droid Sans", "Helvetica Neue", sans-serif',
                      fontSize: '16px',
                      lineHeight: 1.5,
                      fontWeight: 400,
                      color: '#000',
                    }}
                  >
                    {(item.name != null ? String(item.name) : '—')}: {item.value}%
                  </Typography>
                </Box>
              ))}
            </Box>
          </Grid>
        </Grid>
      </React.Fragment>
    );
  };
}

SharedPieChart.propTypes = {
  itemsSelected: PropTypes.array,
  chartClick: PropTypes.func,
  competitors: PropTypes.array,
};

export default SharedPieChart;
